import React, { useState, useRef, useCallback } from 'react';

import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import Modal from '../modal/modal';
import Icon, { COLORS, ICONS } from "../icon";
import WaitingIndicator from '../waiting-indicator/waiting-indicator';
import { UploadFile } from '../../../connection/uploads';
import ImageEditor from '../image-editor/image-editor';
import LargeButton from '../button/large-button';
interface UploadImageModalProps {
	originalImage?: string | null;
	fileTitle?: string;
	allowedTypes?: string[];
	allowedSize?: string;
	open: boolean;
	close: () => void;
	onSave: (image: { original: string, edited: string; }) => void;
	useEditor?: boolean;
}

// combination modal to upload and edit an image
export const UploadImageModal = (props: UploadImageModalProps): JSX.Element => {
	const {
		originalImage,
		fileTitle = "Image",
		allowedTypes = ["image/png", "image/jpeg", "image/gif"],
		allowedSize = "10mb",
		open,
		close,
		onSave,
		useEditor = true,
	} = props;

	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const [uploadedImage, setUploadedImage] = useState<string | null | undefined>(originalImage);
	const [workingFile, setWorkingFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [editing, setEditing] = useState<boolean>(false);

	// prevents user from hitting save before the current edits have been generated into a file
	const handleEditing = useCallback((inProcess: boolean) => {
		setEditing(inProcess);
	}, []);

	const uploadImage = async (file: File | FileList): Promise<string> => {
		setUploading(true);
		setWorkingFile(file as File);
		const uploaded = await UploadFile(user!, token!, file as File);
		setUploading(false);
		return uploaded;
	};

	const handleFile = async (file: File | FileList): Promise<void> => {
		// TODO: error if file is larger than 10mb
		const uploaded = await uploadImage(file);
		setUploadedImage(uploaded);
		if (!useEditor) {
			onSave({ original: uploaded, edited: uploaded });
			close();
		}
	};

	const handleReplace = (e: React.ChangeEvent<HTMLInputElement>): void => {
		if (e.target.files) {
			setWorkingFile(null);
			setUploadedImage(null);
			handleFile(e.target.files[0]);
		}
	};

	const handleCancel = (): void => {
		setWorkingFile(null);
		setUploadedImage(originalImage);
		close();
	};

	const handleSave = async (): Promise<void> => {
		// TODO: if the GET fails this never resolves, need error handling
		const uploaded = await uploadImage(workingFile!);
		onSave({ original: uploadedImage!, edited: uploaded });
		close();
	};

	const fileTypeString: string = allowedTypes.map(type => type.split('/').pop()).join(', ').toUpperCase();
	const subtitle = `Use ${fileTypeString} files up to ${allowedSize} size.`;

	const footer = (
		<div className="image-modal-footer">
			<div>
				{uploadedImage && useEditor && <button className="no-style left-button" onClick={() => inputRef.current?.click()} >
					<Icon name={ICONS.UPLOAD} color={COLORS.CYAN} size={12} />
					{`Replace ${fileTitle}`}
				</button>}
			</div>
			<div>
				<button className="button" onClick={handleCancel}>Cancel</button>
				<button className="button save primary" onClick={handleSave} disabled={!uploadedImage || !workingFile || uploading || editing}>
					{uploading ? <WaitingIndicator /> : "Save"}
				</button>
			</div>
		</div>
	);

	return (
		<Modal
			closeable={false}
			cancellable={true}
			title={uploadedImage ? `Edit ${fileTitle}` : `Upload ${fileTitle}`}
			open={open}
			onRequestClose={close}
			footer={footer}
		>
			{uploadedImage && useEditor ?
				<ImageEditor
					originalImage={uploadedImage}
					onFinished={(file: File) => setWorkingFile(file)}
					setEditing={handleEditing}
				/>
				:
				<LargeButton
					title={`Upload ${fileTitle}`}
					subtitle={subtitle}
					allowedFileTypes={allowedTypes}
					onFile={handleFile}
				/>
			}
			<input
				type="file"
				accept={allowedTypes.join(',') ?? '*'}
				ref={inputRef}
				style={{ display: 'none' }}
				onChange={handleReplace}
			/>
		</Modal>
	);
};